import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableAction, TableActions } from '@app/shared/components/table/table.component';
import { NdfDemandsCalculationsOperation } from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { NdfDemandsCalculationsFilteredSummary } from '@interfaces/a-organizar/ndfDemandsCalculationsFilteredSummary';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';

export interface CardSummaryAction {
  action: string;
  item: NdfDemandsCalculationsFilteredSummary | null;
}

@Component({
  selector: 'app-ndf-card-summary',
  templateUrl: './ndf-card-summary.component.html',
  styleUrls: ['./ndf-card-summary.component.scss'],
  standalone: false,
})
export class NdfCardSummaryComponent extends VaultBaseComponent implements OnInit, OnChanges {
  @Input() ndfDemand: NdfDemandsCalculationsFilteredSummary | null = null;
  @Input() operations: NdfDemandsCalculationsOperation[] = [];
  @Output() sendFillOperation: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() sendCardSummaryAction: EventEmitter<CardSummaryAction> =
    new EventEmitter<CardSummaryAction>();

  derivativeCalculatorPermissions = Permissions.DerivativeCalculator;
  actionIcons: MenuItem[] = [];

  headers: string[] = [];
  columns: string[] = [];
  isCollapsed: boolean | null = null;
  innerOperations: NdfDemandsCalculationsOperation[] = [];

  constructor(
    translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.headers = [
      this.t(this.tk.Management.Id),
      this.t(this.tk.Management.NdfValue),
      this.t(this.tk.Management.NdfOperationStatus),
      this.t(this.tk.Management.NdfFinancialStatus),
      this.t(this.tk.Management.NdfComposition),
      this.t(this.tk.Management.NdfDueDate),
      this.t(this.tk.General.Observation),
    ];
    this.columns = [
      'sequentialId',
      'notionalAmount',
      'operationStatus',
      'financialStatus',
      'composition',
      'dueAt',
      'remarks',
    ];

    this.actionIcons = [
      {
        label: this.t(this.tk.General.Edit),
        icon: 'pi pi-pencil',
        action: 'edit',
      },
      {
        label: this.t(this.tk.General.Duplicate),
        icon: 'pi pi-clone',
        action: 'duplicate',
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'remove',
      },
      {
        label: this.t(this.tk.General.Export1),
        icon: 'pi pi-arrow-up',
        action: 'export',
      },
      {
        label: 'Calculadora de Derivativos',
        icon: 'pi pi-calculator',
        route: `/dashboard/control/derivative-management/derivatives/list?demandId=${this.ndfDemand?.id}`,
      },
      {
        label: 'Operações de Derivativos',
        icon: 'pi pi-chart-bar',
        url: `/dashboard/ndf-operation/list?filter=${this.ndfDemand?.sequentialId}`,
      },
    ];
    this.innerOperations = this.operations.map(operation => {
      const { operationStatus, financialStatus, composition } = operation;
      return {
        ...operation,
        operationStatus: this.getOperationStatusLabel(operationStatus),
        financialStatus: this.getFinancialStatusLabel(financialStatus),
        composition: this.getCompositionLabel(composition),
      };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operations']) {
      const operations = changes['operations'].currentValue as NdfDemandsCalculationsOperation[];
      this.innerOperations = operations.map(operation => {
        const { operationStatus, financialStatus, composition } = operation;
        return {
          ...operation,
          operationStatus: this.getOperationStatusLabel(operationStatus),
          financialStatus: this.getFinancialStatusLabel(financialStatus),
          composition: this.getCompositionLabel(composition),
        };
      });
      this.cdr.detectChanges();
    }
  }

  toggleCollapse(): void {
    if (this.isCollapsed === null) {
      this.isCollapsed = !this.isCollapsed;
      this.sendFillOperation.emit(this.isCollapsed);
      return;
    }
    this.isCollapsed = !this.isCollapsed;
  }

  handleTableAction(tableActions: TableAction<NdfDemandsCalculationsOperation>): void {
    console.log('handleTableAction: ', tableActions);
  }

  handleCardSummaryAction(action: string, item: NdfDemandsCalculationsFilteredSummary | null) {
    this.sendCardSummaryAction.emit({ action, item });
  }

  handleAction(menuItem: MenuItem) {
    const { action } = menuItem;
    this.handleCardSummaryAction(action, this.ndfDemand);
  }

  handleTableSelection({ action, items }: TableActions<NdfDemandsCalculationsOperation>) {
    console.log('handleTableSelection: ', action, items);
  }

  getOperationTypeLabel(operationType: string): string {
    switch (operationType) {
      case 'Loan4131':
        return this.t(this.tk.Management.NdfLoan);
      case 'Import':
        return this.t(this.tk.Management.NdfImport);
      case 'Export':
        return this.t(this.tk.Management.NdfExport);
      case 'Indicative':
        return this.t(this.tk.Management.Indicative);
      default:
        return '';
    }
  }

  getOperationStatusLabel(operationStatus: string = ''): string {
    switch (operationStatus) {
      case 'Simulated':
        return this.t(this.tk.Management.NdfSimulated);
      case 'Quoted':
        return this.t(this.tk.Management.NdfQuoted);
      case 'Hired':
        return this.t(this.tk.Management.NdfHired);
      default:
        return '';
    }
  }

  getFinancialStatusLabel(financialStatus: string = ''): string {
    switch (financialStatus) {
      case 'Open':
        return this.t(this.tk.Management.NdfOpen);
      case 'Settled':
        return this.t(this.tk.Management.NdfSettled);
      case 'Reversed':
        return this.t(this.tk.Management.NdfSettledReversed);
      default:
        return '';
    }
  }

  getCompositionLabel(composition: string = ''): string {
    switch (composition) {
      case 'Full':
        return this.t(this.tk.Management.NdfFull);
      case 'Partial':
        return this.t(this.tk.Management.NdfPartial);
      default:
        return '';
    }
  }

  getSequentialId(): string {
    if (this.ndfDemand?.comexOperation?.sequentialId) {
      return this.ndfDemand.comexOperation.sequentialId;
    }
    if (this.ndfDemand?.loanOperation?.sequentialId) {
      return this.ndfDemand.loanOperation.sequentialId;
    }
    return this.ndfDemand?.sequentialId || '';
  }
  getUrlOperation(): string {
    if (this.ndfDemand?.comexOperation?.sequentialId) {
      return '/dashboard/comex-operation/v1';
    }
    if (this.ndfDemand?.loanOperation?.sequentialId) {
      return '/dashboard/costs/debt/management/list';
    }
    return '/dashboard/ndf-calculator/list';
  }

  getFormattedReference(): string {
    const ref = this.ndfDemand?.reference || '';
    if (ref.includes('WORKING_CAPITAL')) {
      const translated = this.translateService.instant(this.tk.Costs.WorkingCapital);
      return ref.replace('WORKING_CAPITAL', translated);
    }
    return ref;
  }
}
